import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, LogOut, Search, TrendingUp, Star, LayoutDashboard, Users, KeyRound, BarChart3, Trophy, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { getAdminArticles, deleteArticle, verifyAdmin, adminLogout } from '@/lib/api';
import { toast } from 'sonner';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [role, setRole] = useState('admin');
  const [stats, setStats] = useState({
    total: 0,
    news: 0,
    memes: 0,
    'race-updates': 0,
    reels: 0,
    totalViews: 0
  });

  const fetchArticles = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAdminArticles();
      setArticles(data);
      setFilteredArticles(data);
      
      // Calculate stats
      const newStats = {
        total: data.length,
        news: data.filter(a => a.category === 'news').length,
        memes: data.filter(a => a.category === 'memes').length,
        'race-updates': data.filter(a => a.category === 'race-updates').length,
        reels: data.filter(a => a.category === 'reels').length,
        totalViews: data.reduce((sum, a) => sum + (a.views || 0), 0)
      };
      setStats(newStats);
    } catch (error) {
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      const user = await verifyAdmin();
      if (!user) {
        navigate('/admin');
        return;
      }
      setRole(user.role || 'admin');
      fetchArticles();
    };
    checkAuth();
  }, [navigate, fetchArticles]);

  useEffect(() => {
    if (searchQuery) {
      const filtered = articles.filter(
        a => a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
             a.category.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredArticles(filtered);
    } else {
      setFilteredArticles(articles);
    }
  }, [searchQuery, articles]);

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await deleteArticle(deleteId);
      toast.success('Article deleted');
      fetchArticles();
    } catch (error) {
      toast.error('Failed to delete article');
    } finally {
      setDeleteId(null);
    }
  };

  const handleLogout = () => {
    adminLogout();
    toast.success('Logged out');
    navigate('/admin');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const categoryColors = {
    news: 'bg-[#CCFF00] text-black',
    memes: 'bg-purple-500 text-white',
    'race-updates': 'bg-red-500 text-white',
    reels: 'bg-blue-500 text-white',
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-surface border-r border-zinc-800 p-6 hidden lg:block">
        <div className="mb-8">
          <span className="font-heading text-2xl font-black text-[#CCFF00] tracking-tighter">BORMULA</span>
          <p className="font-mono text-xs text-text-secondary uppercase mt-1">Admin Panel</p>
        </div>

        <nav className="space-y-2">
          <div className="flex items-center gap-3 px-4 py-3 bg-[#CCFF00]/10 text-[#CCFF00] rounded-none">
            <LayoutDashboard className="w-5 h-5" />
            <span className="font-body font-medium">Dashboard</span>
          </div>
          {role === 'admin' && (
            <>
              <Link to="/admin/writers" className="flex items-center gap-3 px-4 py-3 text-text-secondary hover:text-white transition-colors" data-testid="writers-nav-link">
                <Users className="w-5 h-5" />
                <span className="font-body font-medium">Writers</span>
              </Link>
              <Link to="/admin/accounts" className="flex items-center gap-3 px-4 py-3 text-text-secondary hover:text-white transition-colors" data-testid="accounts-nav-link">
                <KeyRound className="w-5 h-5" />
                <span className="font-body font-medium">Writer Logins</span>
              </Link>
              <Link to="/admin/analytics" className="flex items-center gap-3 px-4 py-3 text-text-secondary hover:text-white transition-colors" data-testid="analytics-nav-link">
                <BarChart3 className="w-5 h-5" />
                <span className="font-body font-medium">Analytics</span>
              </Link>
              <Link to="/admin/achievements" className="flex items-center gap-3 px-4 py-3 text-text-secondary hover:text-white transition-colors" data-testid="achievements-nav-link">
                <Trophy className="w-5 h-5" />
                <span className="font-body font-medium">Our Story</span>
              </Link>
            </>
          )}
        </nav>

        <div className="absolute bottom-6 left-6 right-6 space-y-4">
          <a 
            href="/" 
            target="_blank"
            className="flex items-center gap-3 px-4 py-3 text-text-secondary hover:text-white transition-colors"
          >
            <Eye className="w-5 h-5" />
            <span className="font-body">View Site</span>
          </a>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 text-text-secondary hover:text-red-400 transition-colors w-full"
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-body">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-8">
        {/* Mobile Header */}
        <div className="lg:hidden mb-8">
          <div className="flex items-center justify-between">
            <span className="font-heading text-2xl font-black text-[#CCFF00]">BORMULA</span>
            <button onClick={handleLogout} className="text-text-secondary" data-testid="mobile-logout-button">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
          {role === 'admin' && (
            <div className="flex gap-1 overflow-x-auto mt-4 -mx-1 px-1" data-testid="mobile-admin-nav">
              <span className="shrink-0 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider bg-[#CCFF00]/10 text-[#CCFF00]">Dashboard</span>
              <Link to="/admin/writers" className="shrink-0 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-text-secondary hover:text-white" data-testid="mobile-nav-writers">Writers</Link>
              <Link to="/admin/accounts" className="shrink-0 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-text-secondary hover:text-white" data-testid="mobile-nav-accounts">Writer Logins</Link>
              <Link to="/admin/analytics" className="shrink-0 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-text-secondary hover:text-white" data-testid="mobile-nav-analytics">Analytics</Link>
              <Link to="/admin/achievements" className="shrink-0 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-text-secondary hover:text-white" data-testid="mobile-nav-achievements">Our Story</Link>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-surface border border-zinc-800 p-6">
            <p className="font-mono text-xs text-text-secondary uppercase">Total Articles</p>
            <p className="font-heading text-3xl font-black text-white mt-2">{stats.total}</p>
          </div>
          <div className="bg-surface border border-zinc-800 p-6">
            <p className="font-mono text-xs text-text-secondary uppercase">Total Views</p>
            <p className="font-heading text-3xl font-black text-[#CCFF00] mt-2">{stats.totalViews.toLocaleString()}</p>
          </div>
          <div className="bg-surface border border-zinc-800 p-6">
            <p className="font-mono text-xs text-text-secondary uppercase">News</p>
            <p className="font-heading text-3xl font-black text-white mt-2">{stats.news}</p>
          </div>
          <div className="bg-surface border border-zinc-800 p-6">
            <p className="font-mono text-xs text-text-secondary uppercase">Memes</p>
            <p className="font-heading text-3xl font-black text-white mt-2">{stats.memes}</p>
          </div>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <h1 className="font-heading text-3xl font-black text-white uppercase">{role === 'writer' ? 'My Articles' : 'Articles'}</h1>
          <Link to="/admin/editor">
            <Button 
              className="bg-[#CCFF00] text-black hover:bg-[#b3e600] font-bold uppercase tracking-wide"
              data-testid="new-article-button"
            >
              <Plus className="w-4 h-4 mr-2" /> New Article
            </Button>
          </Link>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-secondary" />
          <Input
            type="text"
            placeholder="Search articles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 bg-surface border-zinc-800 text-white focus:border-[#CCFF00]"
            data-testid="search-articles"
          />
        </div>

        {/* Articles Table */}
        <div className="bg-surface border border-zinc-800 overflow-hidden">
          {filteredArticles.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left p-4 font-mono text-xs text-text-secondary uppercase">Title</th>
                    <th className="text-left p-4 font-mono text-xs text-text-secondary uppercase hidden md:table-cell">Category</th>
                    <th className="text-left p-4 font-mono text-xs text-text-secondary uppercase hidden lg:table-cell">Date</th>
                    <th className="text-left p-4 font-mono text-xs text-text-secondary uppercase hidden sm:table-cell">Views</th>
                    <th className="text-left p-4 font-mono text-xs text-text-secondary uppercase">Status</th>
                    <th className="text-right p-4 font-mono text-xs text-text-secondary uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredArticles.map((article) => (
                    <tr 
                      key={article.id} 
                      className="border-b border-zinc-800/50 hover:bg-black/50 transition-colors"
                      data-testid={`article-row-${article.id}`}
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {article.image_url && (
                            <img 
                              src={article.image_url}
                              alt=""
                              className="w-12 h-12 object-cover hidden sm:block"
                            />
                          )}
                          <div>
                            <p className="font-body font-medium text-white line-clamp-1">{article.title}</p>
                            <p className="font-body text-xs text-text-secondary line-clamp-1 mt-1">{article.excerpt}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 hidden md:table-cell">
                        <span className={`inline-block px-2 py-1 text-xs font-bold uppercase ${categoryColors[article.category] || categoryColors.news}`}>
                          {article.category}
                        </span>
                      </td>
                      <td className="p-4 hidden lg:table-cell">
                        <span className="font-mono text-sm text-text-secondary">{formatDate(article.created_at)}</span>
                      </td>
                      <td className="p-4 hidden sm:table-cell">
                        <span className="font-mono text-sm text-text-secondary">{article.views?.toLocaleString() || 0}</span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {article.featured && (
                            <Star className="w-4 h-4 text-yellow-500" title="Featured" />
                          )}
                          {article.trending && (
                            <TrendingUp className="w-4 h-4 text-[#CCFF00]" title="Trending" />
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          {(role === 'admin' || (Date.now() - new Date(article.created_at).getTime()) < 600000) ? (
                            <>
                              <Link to={`/admin/editor/${article.id}`}>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="text-text-secondary hover:text-white hover:bg-white/10"
                                  data-testid={`edit-article-${article.id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </Link>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-text-secondary hover:text-red-400 hover:bg-red-500/10"
                                onClick={() => setDeleteId(article.id)}
                                data-testid={`delete-article-${article.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          ) : (
                            <span className="flex items-center gap-1.5 font-mono text-xs text-text-secondary" title="Writers can edit only within 10 minutes of publishing" data-testid={`locked-article-${article.id}`}>
                              <Lock className="w-3 h-3" /> Locked
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="font-heading text-xl text-white mb-2">No articles yet</p>
              <p className="font-body text-text-secondary mb-6">Create your first article to get started</p>
              <Link to="/admin/editor">
                <Button className="bg-[#CCFF00] text-black hover:bg-[#b3e600]">
                  <Plus className="w-4 h-4 mr-2" /> Create Article
                </Button>
              </Link>
            </div>
          )}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-surface border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Article</AlertDialogTitle>
            <AlertDialogDescription className="text-text-secondary">
              Are you sure you want to delete this article? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-zinc-800 text-white hover:bg-white/10">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-500 text-white hover:bg-red-600"
              data-testid="confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
